import { baseValue as baseValueTemplate } from "../contexts/BudgetContext/calc-methods";
import type {
  BaseValue,
  Budget,
  CalcOption,
  MainAppliedTaxes,
} from "../types/budget/budget";
import type {
  AppliedTaxes,
  ServiceData,
  ServiceValues,
  TaxData,
} from "../types/budget/service-item";
import { resolveServiceCalcOption } from "./budget-phase-calc-option";

/**
 * Perfil fiscal da categoria (`baseValues` / `mainAppliedTaxes`) faltando
 * para uma versão em que a categoria tem serviço.
 *
 * Sem a linha, `calculateAndMapService` cai no `baseValue` zerado do fallback
 * e sobrescreve as taxas do serviço com 0 (só tarifa e extras sobrevivem no
 * orçamento). Em forma de cobrança B o Imposto é gross-up sobre a Taxa MM,
 * então zera junto: o cabeçalho da categoria mostra T. Receita e T. Imposto
 * R$ 0 e, depois do autosave, a própria linha do serviço fica zerada.
 *
 * Roda no hydrate ANTES do `RECALCULATE_TOTALS` (ver `commitHydratedBudget`).
 * Não entra no reducer de `RECALCULATE_TOTALS`, que só recalcula totais.
 *
 * Regras:
 * - nunca sobrescreve linha existente;
 * - ordem de origem das taxas: linha irmã (mesma categoria, outra versão,
 *   a mais próxima) → consenso das linhas de serviço da versão → taxas
 *   globais de Totais da MESMA versão (mesma regra de `INIT_BASE_VALUES`);
 * - chave em que os serviços divergem fica no dummy zerado: não escolhe
 *   vencedor;
 * - não cria linha para versão em que a categoria não tem serviço.
 *
 * Pura: devolve a MESMA referência quando nada falta.
 */

type Stage = {
  version: number;
  status: "OPEN" | "NEGOTIATE" | "APPROVED";
};

type GroupEntry = {
  service: ServiceData;
  values: ServiceValues | undefined;
};

type Group = {
  supplierId: string;
  category: string;
  calcOption: CalcOption;
  version: number;
  entries: GroupEntry[];
};

type TaxKey = Exclude<keyof TaxData, "typeNfMm">;

const TAX_KEYS = (
  Object.keys(baseValueTemplate.taxData) as (keyof TaxData)[]
).filter((key) => key !== "typeNfMm" && key !== "tariff") as TaxKey[];

const APPLIED_TAX_DEFAULTS: AppliedTaxes = {
  serviceTax: true,
  iss: true,
  cbs: true,
  ibs: true,
  tribute: true,
  supplier_tribute: true,
  localTax: true,
  tourismTax: true,
  fee: true,
  iof: true,
  markup: true,
  overhead: true,
  commission: true,
  incentive: true,
  doubleCharged: true,
};

const BLANK_DESCRIPTIONS: BaseValue["descriptions"] = {
  composition: "",
  cancelPolicy: "",
  paymentMethod: "",
};

const isApprovedStatus = (status: string | undefined) => {
  const upper = (status || "").toUpperCase();
  return upper === "APPROVED" || upper === "APROVED";
};

/**
 * Etapa de cada versão. Versão 0 é sempre o orçamento. Versão > 0 é
 * fechamento quando o `versionHistory` marca APPROVED (a aprovação grava a
 * versão nova no histórico, em `service.versions` e em `baseValues`); fechamento
 * reprovado não é recalculado e fica de fora; o resto é negociação.
 */
function resolveStage(budget: Budget, version: number): Stage | null {
  if (version === 0) return { version, status: "OPEN" };
  const entry = budget.versionHistory?.find((el) => el.version === version);
  if (isApprovedStatus(entry?.status)) return { version, status: "APPROVED" };
  if ((entry?.status || "").toUpperCase() === "DENIED_CLOSING") return null;
  return { version, status: "NEGOTIATE" };
}

function phaseValues(
  service: ServiceData,
  stage: Stage
): ServiceValues | undefined {
  if (stage.status === "APPROVED") return service.closingValues;
  if (stage.version > 0) return service.negotiationValue?.[stage.version - 1];
  return service.budgetValues;
}

/** Sem `versions` o serviço legado vale para toda etapa: só a versão 0. */
function serviceVersions(service: ServiceData): number[] {
  const versions = Array.isArray(service.versions) ? service.versions : [];
  const valid = versions.filter(
    (version) => Number.isInteger(version) && version >= 0
  );
  return valid.length ? Array.from(new Set(valid)) : [0];
}

function groupKey(
  supplierId: string,
  category: string,
  calcOption: string,
  version: number
) {
  return `${supplierId}|${category}|${calcOption}|${version}`;
}

function collectGroups(budget: Budget): Group[] {
  const groups = new Map<string, Group>();

  for (const service of budget.budgetServices ?? []) {
    if (!service?.supplierId || !service.type || !service.calcOption) continue;

    for (const version of serviceVersions(service)) {
      const stage = resolveStage(budget, version);
      if (!stage) continue;
      if (stage.status === "APPROVED" && !service.closingValues) continue;

      const calcOption = resolveServiceCalcOption(service, stage);
      const key = groupKey(
        service.supplierId,
        service.type,
        calcOption,
        version
      );
      const group =
        groups.get(key) ??
        ({
          supplierId: service.supplierId,
          category: service.type,
          calcOption,
          version,
          entries: [],
        } as Group);
      group.entries.push({ service, values: phaseValues(service, stage) });
      groups.set(key, group);
    }
  }

  return Array.from(groups.values());
}

function sameIdentity(
  row: { supplierId: string; category: string; calcOption: string },
  group: Group
) {
  return (
    row.supplierId === group.supplierId &&
    row.category === group.category &&
    row.calcOption === group.calcOption
  );
}

/** Linha irmã de versão mais próxima; no empate, a versão anterior. */
function closestSibling<T extends { version: number }>(
  rows: T[],
  version: number
): T | undefined {
  return rows
    .filter((row) => row.version !== version)
    .sort(
      (a, b) =>
        Math.abs(a.version - version) - Math.abs(b.version - version) ||
        a.version - b.version
    )[0];
}

function hasAnyNonZeroTax(taxData: Record<string, any> | undefined): boolean {
  if (!taxData) return false;
  return Object.values(taxData).some(
    (taxItem: any) =>
      taxItem &&
      typeof taxItem === "object" &&
      "value" in taxItem &&
      taxItem.value > 0
  );
}

const cloneTemplateTaxData = (): TaxData =>
  JSON.parse(JSON.stringify(baseValueTemplate.taxData));

/**
 * Consenso das linhas: chave em que todas concordam (typeTax + value) é
 * gravada; chave com divergência fica travada no dummy. Chave que nenhuma
 * linha preenche (ou que todas deixam em 0) segue "não definida" e pode
 * herdar das taxas globais.
 */
function taxDataFromConsensus(
  bags: ServiceValues[],
  globalTaxData: Partial<TaxData> | undefined
): TaxData {
  const taxData = cloneTemplateTaxData();
  const unset = new Set<keyof TaxData>();

  for (const key of TAX_KEYS) {
    const present = bags
      .map((bag) => bag.taxData?.[key])
      .filter((tax) => tax && typeof tax === "object");
    if (!present.length) {
      unset.add(key);
      continue;
    }
    const [first] = present;
    const agree = present.every(
      (tax) =>
        tax.typeTax === first.typeTax &&
        Number(tax.value) === Number(first.value)
    );
    if (!agree) continue;
    if (!Number(first.value)) {
      unset.add(key);
      if (first.typeTax)
        taxData[key] = { ...taxData[key], typeTax: first.typeTax };
      continue;
    }
    taxData[key] = {
      ...taxData[key],
      typeTax: first.typeTax,
      value: Number(first.value),
    };
  }

  const nfMm = bags.map((bag) => bag.taxData?.typeNfMm).filter(Boolean);
  if (!nfMm.length) unset.add("typeNfMm");
  else if (nfMm.length === bags.length && nfMm.every((v) => v === nfMm[0])) {
    taxData.typeNfMm = nfMm[0] as CalcOption;
  }

  if (globalTaxData && hasAnyNonZeroTax(globalTaxData)) {
    for (const key of Array.from(unset)) {
      const inherited = globalTaxData[key];
      if (inherited === undefined || inherited === null) continue;
      (taxData as any)[key] =
        typeof inherited === "object" ? { ...inherited } : inherited;
    }
  }

  return taxData;
}

function buildBaseValue(
  budget: Budget,
  group: Group,
  siblings: BaseValue[]
): BaseValue | null {
  const sibling = closestSibling(
    siblings.filter((row) => sameIdentity(row, group)),
    group.version
  );
  if (sibling) {
    return {
      ...sibling,
      version: group.version,
      taxData: JSON.parse(JSON.stringify(sibling.taxData)),
      exchange: { ...sibling.exchange },
      isOverride: sibling.isOverride,
      taxDataOverride: sibling.taxDataOverride,
      // Descritivos são da versão que foi editada — nasce em branco.
      descriptions: { ...BLANK_DESCRIPTIONS },
    };
  }

  const bags = group.entries
    .map((entry) => entry.values)
    .filter((bag): bag is ServiceValues => !!bag?.taxData);
  const globalTaxData = budget.budgetWideAppliedTaxes?.find(
    (el) => el.version === group.version
  )?.taxData;

  if (!bags.length && !hasAnyNonZeroTax(globalTaxData)) return null;

  const taxData = taxDataFromConsensus(bags, globalTaxData);

  // Linha toda zerada por cima de serviço com Taxa MM/Imposto preenchidos
  // gravaria o próprio bug; deixa a lacuna para `INIT_BASE_VALUES`.
  const lineHasFeeOrTribute = bags.some(
    (bag) =>
      Number(bag.taxData?.fee?.value) > 0 ||
      Number(bag.taxData?.tribute?.value) > 0
  );
  if (!hasAnyNonZeroTax(taxData) && lineHasFeeOrTribute) return null;

  return {
    version: group.version,
    supplierId: group.supplierId,
    category: group.category as BaseValue["category"],
    calcOption: group.calcOption,
    taxData,
    exchange: { ...baseValueTemplate.exchange },
    isOverride: false,
    taxDataOverride: false,
    descriptions: { ...BLANK_DESCRIPTIONS },
  };
}

function buildMainAppliedTaxes(
  group: Group,
  siblings: MainAppliedTaxes[]
): MainAppliedTaxes | null {
  const sibling = closestSibling(
    siblings.filter((row) => sameIdentity(row, group)),
    group.version
  );
  if (sibling) {
    return {
      ...sibling,
      version: group.version,
      appliedTaxes: { ...sibling.appliedTaxes },
    };
  }

  const flags = group.entries
    .map((entry) => entry.values?.appliedTaxes)
    .filter((applied): applied is AppliedTaxes => !!applied);
  if (!flags.length) return null;

  const appliedTaxes = { ...APPLIED_TAX_DEFAULTS };
  for (const key of Object.keys(
    APPLIED_TAX_DEFAULTS
  ) as (keyof AppliedTaxes)[]) {
    const values = flags
      .map((applied) => applied[key])
      .filter((value) => value !== undefined && value !== null)
      .map(Boolean);
    if (values.length && values.every((value) => value === values[0])) {
      appliedTaxes[key] = values[0];
    }
  }

  return {
    version: group.version,
    supplierId: group.supplierId,
    category: group.category as MainAppliedTaxes["category"],
    calcOption: group.calcOption,
    appliedTaxes,
  };
}

export function backfillMissingCategoryFiscalRows(budget: Budget): Budget {
  if (!Array.isArray(budget?.budgetServices) || !budget.budgetServices.length) {
    return budget;
  }

  const baseValues = budget.baseValues ?? [];
  const mainAppliedTaxes = budget.mainAppliedTaxes ?? [];
  const newBaseValues: BaseValue[] = [];
  const newMainAppliedTaxes: MainAppliedTaxes[] = [];

  for (const group of collectGroups(budget)) {
    const matches = (
      row: { version: number } & Parameters<typeof sameIdentity>[0]
    ) => sameIdentity(row, group) && row.version === group.version;

    if (!baseValues.some(matches)) {
      const row = buildBaseValue(budget, group, baseValues);
      if (row) newBaseValues.push(row);
    }
    if (!mainAppliedTaxes.some(matches)) {
      const row = buildMainAppliedTaxes(group, mainAppliedTaxes);
      if (row) newMainAppliedTaxes.push(row);
    }
  }

  if (!newBaseValues.length && !newMainAppliedTaxes.length) return budget;

  return {
    ...budget,
    ...(newBaseValues.length
      ? { baseValues: [...baseValues, ...newBaseValues] }
      : {}),
    ...(newMainAppliedTaxes.length
      ? { mainAppliedTaxes: [...mainAppliedTaxes, ...newMainAppliedTaxes] }
      : {}),
  };
}
